/**
 * RYFE mini-router (Block 2) — History API, ~no dependency.
 * react-router-dom was deliberately rejected; this is a ~60-line pushState
 * router. Hash routing is impossible here: the URL fragment is already taken by
 * the share-link decryption key (#4) and Excalidraw's own #json=/#room=.
 *
 * Routes:
 *   /                 → redirect to /files (home dashboard is v1.2)   guarded
 *   /files[/:id]      file browser (current folder id in the path)    guarded
 *   /board/:id        board editor                                    guarded
 *   /md/:id           markdown editor (v1.3.0 Block 4)                 guarded
 *   /library[/:id]    element library                                 guarded
 *   /mcp-tokens       MCP-token management                            guarded
 *   /account          account (change password)                      guarded
 *   /settings         "Meine Einstellungen" (CC_11, D-72)             guarded
 *   /groups/:id       group dashboard (v1.8 WS-Plattform, A4)         guarded
 *   /groups/:id/settings  group settings (members + ACL, A5)          guarded
 *   /s/:shareId       anonymous share view              open (outside auth)
 *   /f/:formId        form by opaque id (CC_11, D-66)   open (form decides)
 */

import { useEffect, useReducer } from "react";

export type RouteName =
  | "home"
  | "private"
  | "systemlists"
  | "files"
  | "board"
  | "md"
  | "list"
  | "library"
  | "mcp-tokens"
  | "ki-chat"
  | "ki-knowledge"
  | "lenses"
  | "account"
  | "settings"
  | "workspace-settings"
  | "shares"
  | "oauth-consent"
  | "shared-with-me"
  | "favorites"
  | "recent"
  | "role-folder"
  | "group"
  | "group-settings"
  | "share"
  | "form"
  | "not-found";

export interface Route {
  name: RouteName;
  params: Record<string, string>;
}

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("popstate", emit);
}

/**
 * AP-3 (P10) — the target for an editor's "← Dateien" button. A file opened from
 * „Für mich freigegeben" carries `?from=shared`; its folder lives in ANOTHER
 * workspace, so returning to `/files/<folderId>` there 404s with a red
 * "not found". In that case go back to the shared view instead.
 */
export const backToFiles = (folderId?: string | null): string => {
  const from =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("from")
      : null;
  if (from === "shared") {
    return "/shared-with-me";
  }
  return folderId ? `/files/${folderId}` : "/files";
};

/** Navigate to an in-app path (pushState by default; replace to avoid history). */
export const navigate = (
  to: string,
  opts: { replace?: boolean } = {},
): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (window.location.pathname + window.location.search === to) {
    return;
  }
  if (opts.replace) {
    window.history.replaceState({}, "", to);
  } else {
    window.history.pushState({}, "", to);
  }
  emit();
};

/** Parse a pathname into a matched route. */
export const matchRoute = (
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
): Route => {
  const p = pathname.replace(/\/+$/, "") || "/";
  let m: RegExpExecArray | null;
  if (p === "/") {
    return { name: "home", params: {} };
  }
  if (p === "/private") {
    return { name: "private", params: {} };
  }
  if (p === "/systemlists") {
    return { name: "systemlists", params: {} };
  }
  if ((m = /^\/files(?:\/([^/]+))?$/.exec(p))) {
    return { name: "files", params: { folderId: m[1] || "" } };
  }
  if ((m = /^\/md\/([^/]+)$/.exec(p))) {
    return { name: "md", params: { id: m[1] } };
  }
  if ((m = /^\/board\/([^/]+)$/.exec(p))) {
    return { name: "board", params: { id: m[1] } };
  }
  if ((m = /^\/list\/([^/]+)$/.exec(p))) {
    return { name: "list", params: { id: m[1] } };
  }
  if ((m = /^\/library(?:\/([^/]+))?$/.exec(p))) {
    return { name: "library", params: { folderId: m[1] || "" } };
  }
  if (p === "/mcp-tokens") {
    return { name: "mcp-tokens", params: {} };
  }
  // v1.6 / D-KI (AP2) — KI-Chat an Dify. Optionaler ?token=<id> initialisiert
  // den Chat im Kontext eines MCP-Tokens (aus dem Token-⋮-Menü).
  if (p === "/ki-chat") {
    return { name: "ki-chat", params: {} };
  }
  // D-121c — editierbare KI-Ordner-Wissens-Seiten (je Intent + Sessions).
  // ?folder=<id>&kind=frage|anweisung|information|entscheidung|sessions
  if (p === "/ki-knowledge") {
    return { name: "ki-knowledge", params: {} };
  }
  // v1.6 / D-KI (AP3) — Systemliste „Lenses".
  if (p === "/lenses") {
    return { name: "lenses", params: {} };
  }
  if (p === "/shared-with-me") {
    return { name: "shared-with-me", params: {} };
  }
  // v1.8 AP-5 — „Dateien"-Unterpunkte: Favoriten (P21), Zuletzt verwendete
  // Ordner (P23), Archiv & Papierkorb (P13; lösen zum Rollen-Ordner des aktiven
  // Scopes auf — WS oder Gruppe, D-132).
  if (p === "/favorites") {
    return { name: "favorites", params: {} };
  }
  if (p === "/recent") {
    return { name: "recent", params: {} };
  }
  if (p === "/archive") {
    return { name: "role-folder", params: { role: "archive" } };
  }
  if (p === "/trash") {
    return { name: "role-folder", params: { role: "trash" } };
  }
  // v1.8 WS-Plattform (D-132) — Gruppen-Dashboard (A4) + Gruppen-Einstellungen
  // (A5). Der aktive Workspace kommt aus dem Kontext (der Router trägt wie überall
  // keine wsId im Pfad), die Gruppe adressiert die :gId — ein bewusst schmalerer
  // Pfad als die Mockup-Skizze `/ws/<wsId>/groups/<gId>` (Datenverkabelung, §0.4).
  if ((m = /^\/groups\/([^/]+)\/settings$/.exec(p))) {
    return { name: "group-settings", params: { groupId: m[1] } };
  }
  if ((m = /^\/groups\/([^/]+)$/.exec(p))) {
    return { name: "group", params: { groupId: m[1] } };
  }
  // Revision R3 — zentrale Freigaben-Verwaltung.
  if (p === "/shares") {
    return { name: "shares", params: {} };
  }
  if (p === "/account") {
    return { name: "account", params: {} };
  }
  // R4 (Punkt 2) — OAuth-Consent für den MCP-Connector (claude.ai). Die
  // OAuth-Parameter reisen im Query-String; die Seite liegt IM Auth-Rumpf,
  // d. h. ohne Login erscheint erst die Login-Maske (URL bleibt erhalten).
  if (p === "/oauth/consent") {
    return { name: "oauth-consent", params: {} };
  }
  if (p === "/settings") {
    return { name: "settings", params: {} };
  }
  if (p === "/workspace-settings") {
    return { name: "workspace-settings", params: {} };
  }
  if ((m = /^\/s\/([^/]+)$/.exec(p))) {
    return { name: "share", params: { shareId: m[1] } };
  }
  if ((m = /^\/f\/([^/]+)$/.exec(p))) {
    return { name: "form", params: { formId: m[1] } };
  }
  return { name: "not-found", params: {} };
};

/**
 * True if the current URL is a view outside the auth hull: the anonymous share
 * view, or a form by opaque id (the form itself decides public vs auth, D-66).
 */
export const isShareRoute = (
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
): boolean => {
  const name = matchRoute(pathname).name;
  return name === "share" || name === "form";
};

/** Subscribe a component to route changes; returns the current matched route. */
export const useRoute = (): Route => {
  const [, force] = useReducer((c) => c + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => {
      listeners.delete(force);
    };
  }, []);
  return matchRoute();
};
